import React, { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, CreditCard, FileText, Calculator, DollarSign, Megaphone, Users, Star, Share2, Plus, Minus } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";

const Billing: React.FC = () => {

  // Until fully implemented
  return <div className="space-y-6">
    <div className="flex items-center justify-between">
      <h1 className="text-3xl font-bold">Budget & Credits</h1>
    </div>
    <p>Coming soon..</p>
  </div>


  const [availableCredits, setAvailableCredits] = useState(200);
  const [usedCredits, setUsedCredits] = useState(800);
  const totalCredits = availableCredits + usedCredits;
  const percentUsed = Math.floor((usedCredits / totalCredits) * 100);

  // Calculator state
  const [dataPoints, setDataPoints] = useState(10);
  const [reviews, setReviews] = useState(0);
  const [influencers, setInfluencers] = useState(0);
  const [calculatedCredits, setCalculatedCredits] = useState(400);
  const [selectedPackage, setSelectedPackage] = useState<string | null>(null);

  // Ref for the calculator section
  const calculatorRef = useRef<HTMLDivElement>(null);

  // Mock function to simulate purchasing credits
  const handleBuyCredits = (amount: number) => {
    // This would integrate with Stripe in a real implementation
    setAvailableCredits(prev => prev + amount);
    toast.success(`Successfully purchased ${amount} credits`);
  };

  // Calculate total credits needed based on user selections
  useEffect(() => {
    const dataPointsCredits = Math.ceil(dataPoints / 10) * 10 * 40;
    const reviewsCredits = reviews * 150;
    const influencerCredits = influencers * 300;
    setCalculatedCredits(dataPointsCredits + reviewsCredits + influencerCredits);
  }, [dataPoints, reviews, influencers]);

  // Handle package selection
  const handlePackageSelect = (packageName: string, dp: number, r: number, inf: number) => {
    setDataPoints(dp);
    setReviews(r);
    setInfluencers(inf);
    setSelectedPackage(packageName);
  };

  // Scroll to calculator section
  const scrollToCalculator = () => {
    calculatorRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Handlers for incrementing/decrementing the values
  const incrementDataPoints = () => {
    setDataPoints(prev => prev + 10);
  };

  const decrementDataPoints = () => {
    setDataPoints(prev => Math.max(10, prev - 10));
  };

  const incrementReviews = () => {
    setReviews(prev => prev + 1);
  };

  const decrementReviews = () => {
    setReviews(prev => Math.max(0, prev - 1));
  };

  const incrementInfluencers = () => {
    setInfluencers(prev => prev + 1);
  };

  const decrementInfluencers = () => {
    setInfluencers(prev => Math.max(0, prev - 1));
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Budget & Credits</h1>
      </div>

      {/* Credit Usage Overview */}
      <Card>
        <CardHeader>
          <CardTitle>Current Credit Usage</CardTitle>
          <CardDescription>Overview of your available and used credits</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between text-sm mb-2">
                <span>Available: {availableCredits} credits</span>
                <span>Used: {usedCredits} credits</span>
              </div>
              <Progress value={percentUsed} className="h-2" />
            </div>

            <div className="pt-4 flex justify-between">
              <Button variant="outline" onClick={() => window.location.href = '/tastermonials'}>
                View Detailed Usage
              </Button>
              <Button onClick={scrollToCalculator}>
                <DollarSign className="mr-2 h-4 w-4" />
                Add Credits
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Pricing Details */}
      <Card>
        <CardHeader>
          <CardTitle>Credit Pricing</CardTitle>
          <CardDescription>What you can unlock with your credits</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-6">
            {/* Data Points */}
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Users className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Data Points</h3>
                  <p className="text-sm text-muted-foreground">40 credits per data point</p>
                </div>
              </div>
              <div className="text-sm text-muted-foreground">
                Access detailed participant data and testing results. Data points are only
                unlockable in groups of 10 (400 credits minimum).
              </div>
            </div>

            {/* Reviews */}
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-yellow-100 rounded-lg">
                  <Star className="h-6 w-6 text-yellow-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Reviews</h3>
                  <p className="text-sm text-muted-foreground">150 credits per review</p>
                </div>
              </div>
              <div className="text-sm text-muted-foreground">
                Verified testimonials from real users that can be used in your marketing
                materials and on your website.
              </div>
            </div>

            {/* Influencer Shares */}
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Share2 className="h-6 w-6 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Influencer Shares</h3>
                  <p className="text-sm text-muted-foreground">300 credits per share</p>
                </div>
              </div>
              <div className="text-sm text-muted-foreground">
                Validated social media posts from influencers showcasing your product with
                data-backed insights.
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Credit Calculator */}
      <Card ref={calculatorRef}>
        <CardHeader>
          <CardTitle>Usage Calculator</CardTitle>
          <CardDescription>Estimate the credits needed for your campaign</CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="calculator" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="calculator">Custom Plan</TabsTrigger>
              <TabsTrigger value="packages">Suggested Packages</TabsTrigger>
            </TabsList>

            <TabsContent value="calculator" className="space-y-6 pt-4">
              <div className="grid md:grid-cols-3 gap-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="dataPoints">Data Points</Label>
                    <span className="text-sm text-muted-foreground">40 credits each</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={decrementDataPoints}
                      className="h-10 w-10 rounded-md flex-shrink-0"
                    >
                      <Minus className="h-4 w-4" />
                    </Button>
                    <div className="flex-1 text-center">
                      <span className="text-xl font-medium">{dataPoints} kits</span>
                      <div className="text-sm text-muted-foreground">{dataPoints * 40} credits</div>
                    </div>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={incrementDataPoints}
                      className="h-10 w-10 rounded-md flex-shrink-0"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="reviews">Reviews</Label>
                    <span className="text-sm text-muted-foreground">150 credits each</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={decrementReviews}
                      className="h-10 w-10 rounded-md flex-shrink-0"
                    >
                      <Minus className="h-4 w-4" />
                    </Button>
                    <div className="flex-1 text-center">
                      <span className="text-xl font-medium">{reviews}</span>
                      <div className="text-sm text-muted-foreground">{reviews * 150} credits</div>
                    </div>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={incrementReviews}
                      className="h-10 w-10 rounded-md flex-shrink-0"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="influencers">Influencer Shares</Label>
                    <span className="text-sm text-muted-foreground">300 credits each</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={decrementInfluencers}
                      className="h-10 w-10 rounded-md flex-shrink-0"
                    >
                      <Minus className="h-4 w-4" />
                    </Button>
                    <div className="flex-1 text-center">
                      <span className="text-xl font-medium">{influencers}</span>
                      <div className="text-sm text-muted-foreground">{influencers * 300} credits</div>
                    </div>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={incrementInfluencers}
                      className="h-10 w-10 rounded-md flex-shrink-0"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>

              {/* Total credits needed */}
              <div className="pt-4 border-t">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Total credits needed</p>
                    <p className="text-2xl font-bold">{calculatedCredits} credits</p>
                  </div>
                  <Button onClick={() => handleBuyCredits(calculatedCredits)}>
                    <CreditCard className="mr-2 h-4 w-4" />
                    Purchase Credits
                  </Button>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="packages" className="pt-4">
              <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
                {/* Starter Package */}
                <Card className={`border-2 ${selectedPackage === 'starter' ? 'border-blue-500' : 'border-transparent'}`}>
                  <CardHeader>
                    <CardTitle>Starter</CardTitle>
                    <CardDescription>Basic data collection</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span>Data Points:</span>
                      <span className="font-medium">10</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span>Reviews:</span>
                      <span className="font-medium">0</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span>Influencer Shares:</span>
                      <span className="font-medium">0</span>
                    </div>
                    <Separator className="my-2" />
                    <div className="flex items-center justify-between">
                      <span className="font-semibold">Total Credits:</span>
                      <span className="font-semibold">400</span>
                    </div>
                  </CardContent>
                  <CardFooter>
                    <Button
                      className="w-full"
                      variant={selectedPackage === 'starter' ? "default" : "outline"}
                      onClick={() => handlePackageSelect('starter', 10, 0, 0)}
                    >
                      {selectedPackage === 'starter' ? 'Selected' : 'Select Package'}
                    </Button>
                  </CardFooter>
                </Card>

                {/* Growth Package */}
                <Card className={`border-2 ${selectedPackage === 'growth' ? 'border-blue-500' : 'border-transparent'}`}>
                  <CardHeader>
                    <CardTitle>Growth</CardTitle>
                    <CardDescription>Data + Marketing material</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span>Data Points:</span>
                      <span className="font-medium">20</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span>Reviews:</span>
                      <span className="font-medium">2</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span>Influencer Shares:</span>
                      <span className="font-medium">1</span>
                    </div>
                    <Separator className="my-2" />
                    <div className="flex items-center justify-between">
                      <span className="font-semibold">Total Credits:</span>
                      <span className="font-semibold">1,100</span>
                    </div>
                  </CardContent>
                  <CardFooter>
                    <Button
                      className="w-full"
                      variant={selectedPackage === 'growth' ? "default" : "outline"}
                      onClick={() => handlePackageSelect('growth', 20, 2, 1)}
                    >
                      {selectedPackage === 'growth' ? 'Selected' : 'Select Package'}
                    </Button>
                  </CardFooter>
                </Card>

                {/* Premium Package */}
                <Card className={`border-2 ${selectedPackage === 'premium' ? 'border-blue-500' : 'border-transparent'}`}>
                  <CardHeader>
                    <CardTitle>Premium</CardTitle>
                    <CardDescription>Complete marketing solution</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span>Data Points:</span>
                      <span className="font-medium">50</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span>Reviews:</span>
                      <span className="font-medium">5</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span>Influencer Shares:</span>
                      <span className="font-medium">3</span>
                    </div>
                    <Separator className="my-2" />
                    <div className="flex items-center justify-between">
                      <span className="font-semibold">Total Credits:</span>
                      <span className="font-semibold">3,650</span>
                    </div>
                  </CardContent>
                  <CardFooter>
                    <Button
                      className="w-full"
                      variant={selectedPackage === 'premium' ? "default" : "outline"}
                      onClick={() => handlePackageSelect('premium', 50, 5, 3)}
                    >
                      {selectedPackage === 'premium' ? 'Selected' : 'Select Package'}
                    </Button>
                  </CardFooter>
                </Card>
              </div>

              <div className="mt-6 p-4 bg-muted rounded-lg">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Selected package</p>
                    <p className="text-2xl font-bold">{calculatedCredits} credits</p>
                  </div>
                  <Button onClick={() => handleBuyCredits(calculatedCredits)}>
                    <CreditCard className="mr-2 h-4 w-4" />
                    Purchase Credits
                  </Button>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Add-on Services */}
      <Card>
        <CardHeader>
          <CardTitle>Add-on Services</CardTitle>
          <CardDescription>Enhance your research with additional services</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-6">
            {/* Science Report */}
            <div className="flex space-x-4">
              <div className="p-2 bg-green-100 rounded-lg h-fit">
                <FileText className="h-6 w-6 text-green-600" />
              </div>
              <div className="space-y-2">
                <h3 className="font-semibold text-lg">Peer-reviewed Science Report</h3>
                <p className="text-sm text-muted-foreground">
                  Get a comprehensive, peer-reviewed scientific report on your product's performance
                  and health impacts. Perfect for regulatory submissions and scientific marketing.
                </p>
                <div className="flex items-center justify-between pt-2">
                  <div className="font-medium">$3,000</div>
                  <Button variant="outline" size="sm">
                    Request Quote
                  </Button>
                </div>
              </div>
            </div>

            {/* Marketing Boost */}
            <div className="flex space-x-4">
              <div className="p-2 bg-orange-100 rounded-lg h-fit">
                <Megaphone className="h-6 w-6 text-orange-600" />
              </div>
              <div className="space-y-2">
                <h3 className="font-semibold text-lg">Marketing Newsletter Boost</h3>
                <p className="text-sm text-muted-foreground">
                  Feature your product in our newsletter reaching 50,000+ health-conscious
                  consumers and industry professionals.
                </p>
                <div className="flex items-center justify-between pt-2">
                  <div className="font-medium">$300</div>
                  <Button variant="outline" size="sm">
                    Learn More
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Billing History section */}
      <Card>
        <CardHeader>
          <CardTitle>Billing History</CardTitle>
          <CardDescription>Your recent transactions and usage</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Description</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead className="text-right">Credit Balance</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow>
                <TableCell>Apr 1, 2025</TableCell>
                <TableCell>Credits Purchase (500)</TableCell>
                <TableCell className="text-right">$250.00</TableCell>
                <TableCell className="text-right">700</TableCell>
              </TableRow>
              <TableRow className="bg-muted/50">
                <TableCell>Apr 2, 2025</TableCell>
                <TableCell>
                  <span className="text-orange-600">Usage:</span> Data Point Unlocks (10)
                </TableCell>
                <TableCell className="text-right">-400 credits</TableCell>
                <TableCell className="text-right">300</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Mar 23, 2025</TableCell>
                <TableCell>Credits Purchase (1,100)</TableCell>
                <TableCell className="text-right">$550.00</TableCell>
                <TableCell className="text-right">200</TableCell>
              </TableRow>
              <TableRow className="bg-muted/50">
                <TableCell>Mar 24, 2025</TableCell>
                <TableCell>
                  <span className="text-orange-600">Usage:</span> Review Unlocks (1)
                </TableCell>
                <TableCell className="text-right">-150 credits</TableCell>
                <TableCell className="text-right">50</TableCell>
              </TableRow>
              <TableRow className="bg-muted/50">
                <TableCell>Mar 25, 2025</TableCell>
                <TableCell>
                  <span className="text-orange-600">Usage:</span> Influencer Share (1)
                </TableCell>
                <TableCell className="text-right">-300 credits</TableCell>
                <TableCell className="text-right">-250</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Mar 1, 2025</TableCell>
                <TableCell>Credits Purchase (200)</TableCell>
                <TableCell className="text-right">$100.00</TableCell>
                <TableCell className="text-right">450</TableCell>
              </TableRow>
              <TableRow className="bg-muted/50">
                <TableCell>Mar 2, 2025</TableCell>
                <TableCell>
                  <span className="text-orange-600">Usage:</span> Data Point Unlocks (10)
                </TableCell>
                <TableCell className="text-right">-400 credits</TableCell>
                <TableCell className="text-right">50</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default Billing;
